import * as CHDeviceSelectors from "@app/project/store/selectors/devices.selectors";

import { EventEmitter, Injectable } from "@angular/core";
import { DeviceClassObjectProperty, SecurityFobInfo, SecurityZoneInfo } from "@app/project/model/ch-device";
import { CHServiceSelectors, DevicesActions } from "@app/project/store";
import { CallState, LoadingState } from "@app/root-store/api-result.state";
import { Store, select } from "@ngrx/store";
import { Observable, combineLatest, from, of } from "rxjs";
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, take, tap } from "rxjs/operators";

import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { ZoneProfile } from "@app/device/components/security/sensor-edit/sensor-edit.component";
import { CHService } from "@app/project/model/ch-service";
import { CHDeviceState } from "@app/project/store/clarehome.state";
import { DeviceDispatchers } from "@app/project/store/dispatchers/device.dispatchers";
import { ActionWord } from "@app/shared/resources/action-word";
import { DEVICE_CLASSES } from "@app/shared/services/equator-api/ClareHomeRpc.constants";
import { EquatorDevicesService } from "@app/shared/services/equator-api/equator-devices";

@Injectable({
    providedIn: "root",
})
export class SecurityDevicesService {
    private readonly INTRUSION_PARTITION_CONFIGURATION = {
        NAME: "com.clarecontrols.equator.hdm.deviceclasses.IntrusionPartitionConfiguration",
        OPERATIONS: {
            LIST_ZONE_PROFILES: "listZoneProfiles",
        },
    };

    public readonly INTRUSION_CS_TEST_SIGNAL = {
        NAME: "com.clarecontrols.equator.hdm.deviceclasses.IntrusionCSTestSignal",
        PROPERTIES: {
            PERIODIC_TEST_SCHEDULE: "periodicTestSchedule",
            NEXT_SCHEDULED_TEST: "nextScheduledTest",
            LAST_SCHEDULED_TEST: "lastScheduledTest",
        },
        OPERATIONS: {
            SEND_TEST_SIGNAL: "sendTestSignal",
        },
    };

    public readonly INTRUSION_ZONE_CONFIGURATION = {
        NAME: "com.clarecontrols.equator.hdm.deviceclasses.IntrusionZoneConfiguration",
        PROPERTIES: {
            ZONE_NAME: "zoneName",
        },
        OPERATIONS: {
            LIST_ZONE_PROFILES: "listZoneProfiles",
            FIND_CURRENT_ZONE_PROFILE: "findCurrentZoneProfile",
            APPLY_ZONE_PROFILE: "applyZoneProfile",
        },
    };
    constructor(
        private readonly deviceDispatcher: DeviceDispatchers,
        private readonly equatorDeviceService: EquatorDevicesService,
        private readonly auditLogService: AuditLogService,
        private readonly store: Store<CHDeviceState>
    ) {}

    /**
     * @description Turns enroll mode on on a ClareOne partition by dispatching a DCO operation call
     * @param {string} uid the uid of the partition device
     * @returns {Observable<null>} the result from the setEnrollMode call
     */
    turnOnEnrollMode$(uid: string): Observable<null> {
        return this.setEnrollMode$(uid, true);
    }

    /**
     * @description Turns enroll mode off on a ClareOne partition by dispatching a DCO operation call
     * @param {string} uid the uid of the partition device
     * @returns {Observable<null>} the result from the setEnrollMode call
     */
    turnOffEnrollMode$(uid: string): Observable<null> {
        return this.setEnrollMode$(uid);
    }

    /**
     * @description Dispatches a DCO property change call to set the enrollMode on a security partition
     * @param {string} uid the partition uid
     * @param {boolean} [isEnabled=false] whether to enable enroll mode or not
     * @returns {Observable<null>} the result from toggling enroll mode
     */
    private setEnrollMode$(uid: string, isEnabled: boolean = false): Observable<null> {
        return this.deviceDispatcher.getUuidForProject().pipe(
            filter(uuid => !!uuid),
            switchMap(
                uuid =>
                    this.equatorDeviceService.setDCOProperty(
                        uuid,
                        uid,
                        this.INTRUSION_PARTITION_CONFIGURATION.NAME,
                        "enrollMode",
                        isEnabled
                    ) as Observable<null>
            ),
            // display an error if there is one
            catchError(() => of(null))
        );
    }

    /**
     * @description Gets the value of the enrollMode property on a security partition
     * @param {string} uuid the controller uuid
     * @param {string} uid the partition uid
     * @returns {Observable<boolean>} whether the controller is in enroll mode
     */
    fetchEnrollMode$(uuid: string, uid: string): Observable<boolean> {
        return this.equatorDeviceService.getDCOProperty<boolean>(uuid, uid, this.INTRUSION_PARTITION_CONFIGURATION.NAME, "enrollMode").pipe(
            tap(isEnabled => {
                this.store.dispatch(
                    new DevicesActions.GetDeviceClassObjectPropertySuccess({
                        uuid,
                        deviceClass: this.INTRUSION_PARTITION_CONFIGURATION.NAME,
                        deviceUid: uid,
                        propertyKey: "enrollMode",
                        propertyValue: isEnabled,
                    })
                );
            })
        );
    }

    /**
     * @description Invokes a DCO operation on a device connected to a server
     * @private
     * @param {Object} obj the param object
     * @param {string} obj.deviceUid the uid of the device on the server to invoke the operation on
     * @param {string} obj.method name of dco operation to invoke
     * @returns {Observable<any>} the result of the DCO invokation
     */
    private doInvokeOperation<T>({ deviceUid, method }: { deviceUid: string; method: string }): Observable<T> {
        return this.deviceDispatcher.getUuidForProject().pipe(
            filter(uuid => !!uuid),
            switchMap(uuid => this.equatorDeviceService.invokeDCOOperation(uuid, deviceUid, this.INTRUSION_ZONE_CONFIGURATION.NAME, method))
        ) as Observable<T>;
    }

    /**
     * @description Fetches the available zone profiles for a sensor from the server
     * @param {string} zoneUid - UID of the zone device
     * @returns { Observable<ZoneProfile[]> } the list of available profiles for the zone
     */
    fetchZoneProfiles$(zoneUid: string): Observable<ZoneProfile[]> {
        return this.doInvokeOperation<ZoneProfile[]>({
            deviceUid: zoneUid,
            method: this.INTRUSION_ZONE_CONFIGURATION.OPERATIONS.LIST_ZONE_PROFILES,
        }).pipe(
            catchError(() => {
                this.deviceDispatcher.dispatchError(null, null, "Unable to load the zone profiles");
                return of([]);
            })
        );
    }

    /**
     * @description Fetches the profile that the zone is currently set to
     * (which really means the collection of all zone settings that matches a profile),
     * or null, which means that the settings don't match any profile,
     * in which case the profile is considered "Custom".
     * @param {string} partitionUid - UID of the partition device
     * @returns { Observable<ZoneProfile> } the current zone profile of the zone device
     */
    fetchCurrentZoneProfile$(partitionUid: string): Observable<ZoneProfile> {
        return this.doInvokeOperation<ZoneProfile>({
            deviceUid: partitionUid,
            method: this.INTRUSION_ZONE_CONFIGURATION.OPERATIONS.FIND_CURRENT_ZONE_PROFILE,
        }).pipe(
            catchError(() => {
                this.deviceDispatcher.dispatchError("Zone Profile", ActionWord.retrieved);
                return of(null);
            })
        );
    }

    /**
     * @description Dispatches an event that deletes a sensor device from the server
     * @param {string} uuid the uuid of the server
     * @param logData
     * @param {(SecurityZoneInfo | SecurityFobInfo)} device the sensor device to remove
     */
    deleteSensor(uuid: string, device: SecurityZoneInfo | SecurityFobInfo, logData?) {
        const deleteCompleteEmitter = new EventEmitter();
        deleteCompleteEmitter.pipe(take(1)).subscribe(() => {
            this.deviceDispatcher.resetDevicesCallState();
            if (logData) {
                const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.CLAREONE, LOG_SUB_TYPE.SENSOR_FOBS_DELETE);
                this.auditLogService.logEvent(logKey, {
                    optionalParams: { uuid },
                    projectId: logData.project?.id,
                    entityId: logData.project?.entityId,
                    data: `Zone Type: ${logData.zoneName}, Zone Number: ${device.zoneNumber}, Zone Name: ${device.name}`,
                });
            }
        });
        this.deviceDispatcher.deleteSensor(uuid, device, deleteCompleteEmitter);
    }

    /**
     * @description Selects the device class information for a given fob device
     * @param {SecurityFobInfo} device the fob device to select the DCOs for
     * @returns {Observable<{ zoneConfigDCOCallState: CallState<any> }>} dco info for the device
     */
    selectSecurityFobDeviceClass$(device: SecurityFobInfo): Observable<{ zoneConfigDCOCallState: CallState<any> }> {
        return this.store
            .pipe(select(CHDeviceSelectors.selectDeviceClassObjectState(device.uid, DEVICE_CLASSES.INTRUSION_ZONE_CONFIG)))
            .pipe(
                distinctUntilChanged(),
                map(zoneConfigDCOState => ({
                    zoneConfigDCOCallState: zoneConfigDCOState?.callState,
                })),
                // if the state is loaded or an error, emit a value
                filter(states => states?.zoneConfigDCOCallState === LoadingState.loaded)
            );
    }

    /**
     * @description Selects the device class information for a given sensor device
     * @param {SecurityZoneInfo} device the sensor device to select the DCOs for
     * @returns {Observable<{ zoneDCOCallState: CallState<any>; zoneConfigDCOCallState: CallState<any> }>} dco info for the device
     */
    selectSecurityZoneDeviceClass$(
        device: SecurityZoneInfo
    ): Observable<{ zoneDCOCallState: CallState<any>; zoneConfigDCOCallState: CallState<any> }> {
        return combineLatest([
            this.store.pipe(select(CHDeviceSelectors.selectDeviceClassObjectState(device.uid, DEVICE_CLASSES.INTRUSION_ZONE_CONFIG))),
            this.store.pipe(select(CHDeviceSelectors.selectDeviceClassObjectState(device.uid, DEVICE_CLASSES.INTRUSION_ZONE))),
        ]).pipe(
            distinctUntilChanged(),
            map(([zoneConfigDCOState, zoneDCOState]) => ({
                zoneDCOCallState: zoneDCOState?.callState,
                zoneConfigDCOCallState: zoneConfigDCOState?.callState,
            })),
            // if the state is loaded or an error, emit a value
            filter(states => states?.zoneDCOCallState === LoadingState.loaded && states?.zoneConfigDCOCallState === LoadingState.loaded)
        );
    }

    /**
     * @description Fetches the intrusionPartition device class that holds the armed status of the partition
     * @param {string} uuid the uuid of the server
     * @param {string} deviceUid the uid of the partition device on the server
     * @returns {Observable<Record<string, boolean>>} the armed statuses of the partition
     */
    fetchPartitionArmedStatus$(uuid: string, deviceUid: string): Observable<Record<string, boolean>> {
        return this.deviceDispatcher.selectDeviceClassObject$(uuid, deviceUid, DEVICE_CLASSES.INTRUSION_PARTITION).pipe(
            distinctUntilChanged(),
            map((deviceClass: { Properties: Record<string, DeviceClassObjectProperty> }) => {
                const armedStatuses = {};

                if (deviceClass) {
                    const deviceClassProps = deviceClass.Properties || {};
                    ["armedStay", "armedAway", "armed", "armingStay", "armingAway", "arming", "readyToArm"].forEach(
                        property => (armedStatuses[property] = deviceClassProps[property]?.Value as boolean)
                    );
                    if (deviceClass.Properties?.hasOwnProperty("state") && deviceClass.Properties["state"]) {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        const state = deviceClass.Properties["state"].Value;
                        if (state === 2) {
                            armedStatuses["armedAway"] = true;
                        } else if (state === 3) {
                            armedStatuses["armedStay"] = true;
                        }
                    }
                }
                return armedStatuses;
            }),
            distinctUntilChanged(),
            filter(armedStatuses => Object.values(armedStatuses).some(status => status !== undefined))
        );
    }

    /**
     * @description Fetches the intrusionZone and intrusionZoneConfig device classes for the given security sensors from the server.
     * Once the requests are dispatched the device class info for those sensors are selected from the store an transformed into
     * securityZoneInfo objects.
     * @param {string} uuid the server uuid
     * @param {SecurityZoneInfo[]} devices the array of sensor devices
     * @param {Record<string, boolean>} armedStatuses the armed state of the partition
     * @returns {Observable<SecurityZoneInfo[]>} all the security zone info
     */
    fetchSecurityZoneDeviceClasses$(
        uuid: string,
        devices: SecurityZoneInfo[],
        armedStatuses: Record<string, boolean>
    ): Observable<SecurityZoneInfo[]> {
        // Gather all sensor UIDs
        const deviceUids = devices.reduce((uids: string[], device) => {
            uids.push(device.uid);
            return uids;
        }, []);

        // Dispatch the events to get the sensor device classes
        this.store.dispatch(new DevicesActions.GetDeviceClassObjectsForDevices({ uuid, deviceUids, dco: DEVICE_CLASSES.INTRUSION_ZONE }));
        this.store.dispatch(
            new DevicesActions.GetDeviceClassObjectsForDevices({
                uuid,
                deviceUids,
                dco: DEVICE_CLASSES.INTRUSION_ZONE_CONFIG,
                includeMetadata: true,
            })
        );

        // Return the sensor devices, complete with device class info (reactively, when it loads)
        return from(devices).pipe(
            mergeMap(device =>
                // build up the array of UIDs, fetch the data
                // but still select each slice of state for the device individually
                this.selectSecurityZoneDeviceClass$(device).pipe(
                    take(1),
                    switchMap(_ => this.store.pipe(select(CHDeviceSelectors.getZoneDeviceDcoInfo(devices, armedStatuses))))
                )
            )
        );
    }

    /**
     * @description Fetches the intrusionZone and intrusionZoneConfig device classes for the given security sensors from the server.
     * Once the requests are dispatched the device class info for those sensors are selected from the store an transformed into
     * securityZoneInfo objects.
     * @param {string} uuid the server uuid
     * @param {SecurityZoneInfo[]} devices the array of sensor devices
     * @returns {Observable<SecurityZoneInfo[]>} all the security zone info
     */
    fetchSecurityFobDeviceClasses$(uuid: string, devices: SecurityFobInfo[]): Observable<SecurityFobInfo[]> {
        // Gather all sensor UIDs
        const deviceUids = devices.reduce((uids: string[], device) => {
            uids.push(device.uid);
            return uids;
        }, []);

        // Dispatch the events to get the sensor device classes
        this.store.dispatch(
            new DevicesActions.GetDeviceClassObjectsForDevices({
                uuid,
                deviceUids,
                dco: DEVICE_CLASSES.INTRUSION_ZONE_CONFIG,
                includeMetadata: true,
            })
        );

        // Return the sensor devices, complete with device class info (reactively, when it loads)
        return from(devices).pipe(
            mergeMap(device =>
                // build up the array of UIDs, fetch the data
                // but still select each slice of state for the device individually
                this.selectSecurityFobDeviceClass$(device).pipe(
                    take(1),
                    switchMap(_ => this.store.pipe(select(CHDeviceSelectors.getFobDeviceDcoInfo(devices))))
                )
            )
        );
    }

    /**
     * @description Selects the security fob devices based on fob control points on the partition service.
     * fob config control points are enumerated from 1 - 10. They can be fob or pendant devices.
     * @param {CHService} securityPartition the security partition service on the server
     * @returns {Observable<SecurityFobInfo[]>} the array of security fob devices info
     */
    public selectSecurityFobDevicesByService$(securityPartition: CHService): Observable<SecurityFobInfo[]> {
        return this.store.pipe(select(CHServiceSelectors.selectSecuritySensorList(securityPartition, "fob_config_")));
    }

    /**
     * @description Selects the security zone devices based on zone control points on the partition service.
     * zone config control points are enumerated from 1 - 255.
     * @param {CHService} securityPartition the security partition service on the server
     * @returns {Observable<SecurityZoneInfo[]>} the array of security zone devices info
     */
    public selectSecurityZoneDevicesByUid$(securityPartition: CHService): Observable<SecurityZoneInfo[]> {
        return this.store.pipe(select(CHServiceSelectors.selectSecuritySensorList(securityPartition, "zone_config_")));
    }

    /**
     * @description Selects a security zone by its uid. Some basic device info and its device class info are returned
     * @param {string} deviceUid the zone device uid
     * @returns {Observable<SecurityZoneInfo>} the security zone info for the device
     */
    public selectSecurityZoneByUid$(deviceUid: string): Observable<SecurityZoneInfo> {
        return this.store.pipe(select(CHDeviceSelectors.getSecurityZoneByUID(deviceUid)));
    }
}
